/**
 * The light half of the filter card family (FRIDGE-1088): the brand line, the
 * designed no-photo brand tile and the FFN chip. Kept apart from
 * ./filter-card.tsx on purpose — the appliance page's compatible-filter list
 * (components/brand-kit/compatible-filter-card.tsx) is reachable from the
 * brand-kit barrel on every collection and filter page, and importing the tile
 * from the card module dragged the whole option card, its price line and the
 * pack arithmetic into those routes' first-load JS (+7.3 kB on
 * /collections/[slug], measured against main 09/19/2026). This module imports
 * nothing but the FFN format check.
 */
import { isValidFfn } from '@/lib/parts/fpn'

/** The brand line: logo when the brand has one, else the name. */
export function FilterBrandMark({ brand, logo }: { brand: string | null | undefined; logo: string | null | undefined }) {
  if (logo) {
    return (
      <p className="filter-brand-mark">
        {/* eslint-disable-next-line @next/next/no-img-element -- a logo of unknown aspect at a fixed height; sized in globals.css */}
        {/* FRIDGE-1540: the mark's 1.25rem height and 6.5rem width cap inline, so Chromium counts the lazy
            logo as sized; object-fit contain at the left draws it as before. */}
        <img src={logo} alt={brand ?? ''} loading="lazy" style={{ height: '1.25rem', width: '6.5rem' }} />
      </p>
    )
  }
  if (!brand) return null
  return <p className="cat-card__brand">{brand}</p>
}

/** The (FFN) Fridge.com Filter Number on a card — Real-or-Hidden, one class. */
export function FfnChip({ ffn }: { ffn: string | null | undefined }) {
  if (!isValidFfn(ffn)) return null
  return <span className="ffn-chip">{ffn}</span>
}

/**
 * The designed no-photo tile. Decorative (aria-hidden): the card's own brand
 * line, title and part number carry the same words for assistive tech.
 */
export function FilterBrandTile({
  brand,
  logo,
  code,
  size = 'md',
}: {
  brand: string | null | undefined
  logo: string | null | undefined
  code?: string | null
  /** xs: a thumbnail (~40–56 px) — the logo or the brand's initials only, no part number. */
  size?: 'xs' | 'sm' | 'md'
}) {
  const xs = size === 'xs'
  return (
    <div
      className={`filter-brand-tile${size === 'md' ? '' : ` filter-brand-tile--${size}`}`}
      data-filter-brand-tile={logo ? 'logo' : 'name'}
      aria-hidden="true"
    >
      {logo ? (
        // eslint-disable-next-line @next/next/no-img-element -- decorative logo inside the tile; sized in globals.css
        <img className="filter-brand-tile__logo" src={logo} alt="" loading="lazy" />
      ) : (
        <span className="filter-brand-tile__name">{xs ? brandInitials(brand) : brand || 'Water filter'}</span>
      )}
      {code && !xs ? <span className="filter-brand-tile__code">{code}</span> : null}
    </div>
  )
}

/** "Waterdrop" → "W", "Glacier Fresh" → "GF", "LG" → "LG": what fits a thumbnail tile. */
export function brandInitials(brand: string | null | undefined): string {
  const words = (brand ?? '').replace(/\(.*?\)/g, '').trim().split(/[\s&+\-']+/).filter(Boolean)
  if (words.length === 0) return '·'
  if (words.length === 1) return words[0].length <= 3 && words[0] === words[0].toUpperCase() ? words[0] : words[0][0].toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}
